import Porthole from "porthole";
import reduxStore from "utils/storeFactory";
import screenfull from "screenfull";
import getAllHotspots from "selectors/hotspot/getAllHotspots";
import * as ProcessExternalMessageReceivedType from "./ProcessExternalMessageReceivedType";
import * as EventType from "./EventType";

const reduxState = () => reduxStore.getState();
const windowProxy = new Porthole.WindowProxy("https://verse.com/proxy.html");

let canUseBasicApi;
let canUseAdvancedApi;
let useLegacyApi;
let storyIsAvailable = false;
const messageQueue = [];

const unsubscribeInit = reduxStore.subscribe(initEventsTracking);

function initEventsTracking() {
  const state = reduxState();

  const isSingleVideo = state.playerOptions.singleVideoPlayer;
  if (isSingleVideo) return;

  if (!state.story) return;

  storyIsAvailable = true;

  messageQueue.forEach((message) => {
    sendMessage(message);
  });

  useLegacyApi = !!state.story.use_legacy_api;

  // fallback array only exists because most simulatedResponses don't include permissions yet
  const userPermissions = state.story.permissions || [];
  const canSendAnalytics =
    userPermissions.includes("verse.custom_analytics") || isSingleVideo;
  canUseBasicApi =
    userPermissions.includes("verse.basic_api") ||
    userPermissions.includes("verse.standard_api") ||
    isSingleVideo;
  canUseAdvancedApi =
    userPermissions.includes("verse.advanced_api") || isSingleVideo;

  // load listener only if both api settings are not present
  if (useLegacyApi) {
    // windowProxy included for backwards compatibility
    windowProxy.addEventListener(processMessage);
  }

  // listen only if user has an API permission
  if ((canSendAnalytics || canUseBasicApi) && !useLegacyApi) {
    window.addEventListener("message", processMessage, false);
  }

  if (canUseAdvancedApi && !useLegacyApi) {
    window.addEventListener("message", processAdvancedMessage, false);
  }

  notifyExternalOfApiConfiguration();
  unsubscribeInit();

  /**
   * Track the last notifyVideoChange emit videoId so we don't send repeats
   */
  let previousVideoChangeVideoId = null;
  window.VersePlayer.visibleTileEmitter.addListener(
    "notifyVideoChange",
    (tileConfig) => {
      if (tileConfig.videoId !== previousVideoChangeVideoId) {
        previousVideoChangeVideoId = tileConfig.videoId;

        sendMessage({
          eventType: EventType.SHOW_TILE_CONTROLS,
          tileConfig: { rows: tileConfig.rows, columns: tileConfig.columns },
        });
      }
    },
  );
}

/**
 * Prcesses message data and triggers action if matching.
 *
 * @param {Object} event - data from the postMessage or proxy event
 */
function processMessage(event) {
  const message = event.data;
  const action = message.action || message;

  if (action) {
    if (action === ProcessExternalMessageReceivedType.PLAY) {
      // Resume playback
      window.VersePlayer.play();
    } else if (action === ProcessExternalMessageReceivedType.PAUSE) {
      // Pause playback
      window.VersePlayer.pause();
    }
  }

  // Otherwise, do nothing.
}

function processAdvancedMessage(event) {
  const message = event.data;
  const action = message.action;
  if (action) {
    switch (action) {
      case ProcessExternalMessageReceivedType.SEEK:
        window.VersePlayer.seek(message.time);
        break;
      case ProcessExternalMessageReceivedType.NEXT_CHAPTER:
        window.VersePlayer.goToNextChapter();
        break;
      case ProcessExternalMessageReceivedType.PREVIOUS_CHAPTER:
        window.VersePlayer.goToPreviousChapter();
        break;
      case ProcessExternalMessageReceivedType.GO_TO_CHAPTER:
        if (message.chapterTitle) {
          window.VersePlayer.goToChapterTitle(message.chapterTitle);
        } else if (message.index) {
          window.VersePlayer.goToChapterAtIndex(parseInt(message.index, 10));
        }
        break;
      case ProcessExternalMessageReceivedType.GO_TO_CHAPTER_ID:
        window.VersePlayer.goToChapter(message.chapterId);
        break;
      case ProcessExternalMessageReceivedType.NEXT_SLIDE:
        window.VersePlayer.goToNextSlide();
        break;
      case ProcessExternalMessageReceivedType.PREVIOUS_SLIDE:
        window.VersePlayer.goToPreviousSlide();
        break;
      case ProcessExternalMessageReceivedType.GO_TO_SLIDE:
        window.VersePlayer.goToSlideAtIndex(message.index);
        break;
      case ProcessExternalMessageReceivedType.TOGGLE_CHAPTER_LIST_VISIBILITY:
        window.VersePlayer.toggleChapterListVisbility();
        break;
      case ProcessExternalMessageReceivedType.SET_CHAPTER_LIST_VISIBILITY:
        window.VersePlayer.setChapterListVisbility(message.visible);
        break;
      case ProcessExternalMessageReceivedType.NAVIGATE_BACK:
        window.VersePlayer.navigateBack();
        break;
      case ProcessExternalMessageReceivedType.TOGGLE_FULLSCREEN:
        if (screenfull.enabled) {
          if (screenfull.isFullscreen) {
            screenfull.exit();
          } else {
            screenfull.request();
          }
        }
        break;
      case ProcessExternalMessageReceivedType.SET_CAPTION_LANGUAGE:
        window.VersePlayer.setCaptionLanguage(message.languageCode);
        break;
      case ProcessExternalMessageReceivedType.SET_VISIBLE_TILE:
        window.VersePlayer.visibleTileEmitter.emit("setVisibleTile", {
          row: parseInt(message.row, 10),
          column: parseInt(message.column, 10),
        });
        break;
      default:
        // no op
        break;
    }
  }
}

/**
 * Looks to see if message is from iframe and will post to parent
 *
 * @param {Object} message - message object for parent info
 * @param {Boolean} notifyAll - if true, the message will be sent regardless of AdvancedApi permission
 */
function sendMessage(message, notifyAll) {
  function submitMessage(mess) {
    // Find out if the the player is embedded
    const embedded = window.parent !== window;
    try {
      window.postMessage(mess, "*");
    } catch {
      // Swallow all errors.
    }
    if (!embedded) {
      // This is not an embedded player so these messages don't do anything. Just `console.log`
      console.log("send external message", message); //eslint-disable-line no-console
    } else if (!!notifyAll || (canUseAdvancedApi && !useLegacyApi)) {
      window.parent.window.postMessage(mess, "*");
    } else {
      // porthole legacy
      windowProxy.post(mess);
    }
  }

  if (!storyIsAvailable) {
    return messageQueue.push(message);
  }

  return submitMessage(message);
}

function notifyExternalOfApiConfiguration() {
  const state = reduxStore.getState();
  const hotspots = getAllHotspots(state);

  const callbackHotspots = Object.values(hotspots)
    .map((hotspot) => {
      if (hotspot.target && hotspot.target.identifier) {
        return {
          startTime: hotspot.startTime,
          identifier: hotspot.target.identifier,
          title: hotspot.title,
        };
      }
      return null;
    })
    .reduce((memo, val) => {
      if (val) {
        memo.push(val);
      }
      return memo;
    }, []);
  const apiStoryConfig = {
    callbackHotspots,
    capabilities: {
      canUseStandardApi: canUseBasicApi,
      canUseAdvancedApi,
    },
  };
  sendMessage({
    eventType: EventType.STORY_METADATA,
    storyConfig: apiStoryConfig,
  });
}

export function notifyExternalOfNavigation(path) {
  sendMessage(
    {
      eventType: EventType.NAVIGATION,
      path,
    },
    true,
  );
}

export function notifyExternalOfChapterEnd(input) {
  sendMessage({
    eventType: EventType.CHAPTER_END,
    chapterId: input.chapterId,
    chapterTitle: input.chapterTitle,
  });
}

export function notifyExternalOfHotspotClick(identifier) {
  sendMessage({
    eventType: EventType.HOTSPOT_CLICK,
    identifier,
  });
}

/**
 * Most recent media playback value that was reported
 *
 * We'll use this to ensure that we don't send duplicate events.
 */
let lastMediaPlaybackData;

export function notifyExternalMediaPlayback(data) {
  if (
    data.percentagePlayed === lastMediaPlaybackData?.percentagePlayed &&
    data.videoId === lastMediaPlaybackData?.videoId
  ) {
    return;
  }

  sendMessage({
    eventType: EventType.MEDIA_PLAYBACK,
    ...data,
  });

  lastMediaPlaybackData = { ...data };
}

export function notifyExternalOfAnalyticsTracking(trackObj) {
  sendMessage({
    eventType: EventType.ANALYTICS_TRACK,
    ...trackObj,
  });
}
